import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'destructive' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm';

/*
 * Basepoint buttons: flat, a 1px border, soft corners, weight 500, and a
 * one-pixel press. Primary is ink (one per screen); secondary is the hairline
 * button on the field fill; outline is the quiet ink fill; ghost is text;
 * destructive is the quiet red hairline and danger the solid red confirm.
 * No shadow on any of them.
 */
const base =
  'relative inline-flex shrink-0 select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-md border font-medium ' +
  'transition-[transform,background-color,border-color,color] duration-[120ms] ease-out ' +
  'active:translate-y-px disabled:pointer-events-none disabled:opacity-45 aria-disabled:pointer-events-none aria-disabled:opacity-45 ' +
  '[&_svg]:size-4 [&_svg]:shrink-0';

const variants: Record<ButtonVariant, string> = {
  primary: 'border-primary bg-primary text-on-primary hover:border-primary-hover hover:bg-primary-hover',
  secondary:
    'border-border-strong bg-[var(--bp-field-fill)] text-fg hover:[background:linear-gradient(var(--bg-hover),var(--bg-hover)),var(--bp-field-fill)]',
  outline: 'border-transparent bg-active text-fg hover:bg-border-strong',
  ghost: 'border-transparent bg-transparent text-fg-muted hover:bg-hover hover:text-fg',
  destructive:
    'border-[color-mix(in_srgb,var(--bp-danger)_45%,transparent)] bg-transparent text-danger hover:bg-[color-mix(in_srgb,var(--bp-danger)_10%,transparent)]',
  danger:
    'border-danger bg-danger text-surface hover:border-danger hover:bg-[color-mix(in_srgb,var(--bp-danger)_88%,var(--bp-text))]',
  link: 'h-auto min-h-0 border-transparent px-0 text-link hover:underline',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-7 px-2 text-sm',
  md: 'h-8 px-2.5 text-base',
  lg: 'h-9 px-3 text-md',
  icon: 'size-8 px-0',
  'icon-sm': 'size-7 px-0',
};

export function buttonClass(
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'md',
  className?: string,
): string {
  return cn(base, variants[variant], variant === 'link' ? '' : sizes[size], className);
}
